<svelte:options customElement="hb-input-datetime" />

<script lang="ts">
  /**
   * Svelte Input Datetime WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type { Component } from "./types/webcomponent.type";
  import { addComponent } from "wc-js-utils";
  import type { Events as inputDateEvents } from "../input-date/types/webcomponent.type";
  import type { Events as inputNumberEvents } from "../input-number/types/webcomponent.type";
  import pkg from "../../../package.json";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let {
    schemaentry,
    show_validation = "no",
  }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let valid = $state(false);
  let value_date: string | undefined = $state(undefined);
  let value_hours: number | undefined = $state(undefined);
  let value_minutes: number | undefined = $state(undefined);
  let value_seconds: number | undefined = $state(undefined);

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }
  addComponent({
    repoName: "@htmlbricks/hb-input-number",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-date",
    version: pkg.version,
  });

  let value: string | undefined = $derived.by(() => {
    if (
      value_date &&
      (value_seconds || value_seconds === 0) &&
      (value_hours || value_hours === 0) &&
      (value_minutes || value_minutes === 0)
    ) {
      try {
        return new Date(
          `${value_date} ${value_hours.toString().padStart(2, "0")}:${value_minutes.toString().padStart(2, "0")}:${
            value_seconds.toString().padStart(2, "0") || "00"
          }`,
        ).toISOString();
      } catch {
        return undefined;
      }
    }
    return undefined;
  });

  let schemaDate: Component["schemaentry"] | undefined = $derived.by(() => {
    return {
      id: "date",
      value: value_date || undefined,
      required: true,
      disabled: !!field?.disabled,
    };
  });
  let schemaHours: Component["schemaentry"] | undefined = $derived.by(() => {
    return {
      id: "hours",
      value: value_hours || undefined,
      required: true,
      disabled: !!field?.disabled,
      params: {
        min: 0,
        max: 23,
      },
    };
  });
  let schemaMinutes: Component["schemaentry"] | undefined = $derived.by(() => {
    return {
      id: "minutes",
      value: value_minutes || undefined,
      required: true,
      disabled: !!field?.disabled,
      params: {
        min: 0,
        max: 59,
      },
    };
  });
  let schemaSeconds: Component["schemaentry"] | undefined = $derived.by(() => {
    return {
      id: "seconds",
      value: value_seconds || undefined,
      required: true,
      disabled: !!field?.disabled,
      params: {
        min: 0,
        max: 59,
      },
    };
  });

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      valid = false;
      return;
    }

    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      if (nextField.value) {
        try {
          const date = new Date(nextField.value as string | number | Date);
          if (
            date &&
            date.valueOf() > 10000 &&
            date.valueOf() < 10000000000000
          ) {
            value_date = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
            value_hours = date.getHours();
            value_minutes = date.getMinutes();
            value_seconds = date.getSeconds();
          }
        } catch {
          /* ignore */
        }
      }
    }

    const f = nextField;
    if (f.required) {
      if (value) {
        let min_date: Date | undefined = undefined;
        let max_date: Date | undefined = undefined;
        if (f.params?.min) {
          min_date = new Date(f.params.min);
        }
        if (f.params?.max) {
          max_date = new Date(f.params.max);
        }

        const valueDateIs = new Date(value);

        if (min_date && !(valueDateIs.valueOf() >= min_date.valueOf())) {
          valid = false;
        } else if (
          max_date &&
          !(valueDateIs.valueOf() <= max_date.valueOf())
        ) {
          valid = false;
        } else {
          valid = true;
        }
      } else {
        valid = false;
      }
    } else {
      valid = true;
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = {
      value,
      valid,
      id: f.id as string | undefined,
    };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });

  function changeDate(e: string) {
    value_date = e;
  }

  function changeHour(e: number) {
    value_hours = e;
    if (value_minutes === undefined || value_minutes === null) {
      value_minutes = 0;
    }
    if (value_seconds === undefined || value_seconds === null) {
      value_seconds = 0;
    }
  }

  function changeMinutes(e: number) {
    value_minutes = e;
    if (value_seconds === undefined || value_seconds === null) {
      value_seconds = 0;
    }
  }

  function changeSeconds(e: number) {
    value_seconds = e;
  }
</script>

{#if field != null}
  <div class="field">
    {#if schemaDate && schemaHours && schemaMinutes && schemaSeconds}
      <div class="field is-grouped is-grouped-multiline hb-input-datetime-group">
        <div class="control">
          <hb-input-date
            onsetVal={(e: { detail: inputDateEvents["setVal"] }) => {
              if (e.detail?.value) changeDate(e.detail.value);
            }}
            schemaentry={JSON.stringify(schemaDate)}
          ></hb-input-date>
        </div>
        <div class="control">
          <hb-input-number
            onsetVal={(e: { detail: inputNumberEvents["setVal"] }) => {
              if (e.detail?.value || Number(e.detail.value) === 0)
                changeHour(e.detail.value);
            }}
            schemaentry={JSON.stringify(schemaHours)}
          ></hb-input-number>
        </div>
        <div class="control hb-input-datetime-sep-wrap" aria-hidden="true">
          <span class="hb-input-datetime-sep">:</span>
        </div>
        <div class="control">
          <hb-input-number
            onsetVal={(e: { detail: inputNumberEvents["setVal"] }) => {
              if (e.detail?.value || Number(e.detail.value) === 0)
                changeMinutes(e.detail.value);
            }}
            schemaentry={JSON.stringify(schemaMinutes)}
          ></hb-input-number>
        </div>

        {#if !field?.params?.removeSeconds}
          <div class="control hb-input-datetime-sep-wrap" aria-hidden="true">
            <span class="hb-input-datetime-sep">:</span>
          </div>
          <div class="control">
            <hb-input-number
              onsetVal={(e: { detail: inputNumberEvents["setVal"] }) => {
                if (e.detail?.value || Number(e?.detail?.value) === 0)
                  changeSeconds(e.detail.value);
              }}
              schemaentry={JSON.stringify(schemaSeconds)}
            ></hb-input-number>
          </div>
        {/if}
      </div>
    {/if}

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
